// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadPopularCategories from "@mcmc/App/Handlers/PopularCategories/LoadPopularCategories";
import { PopularCategoriesCollectionModel } from "@mcmc/App/Services/PopularCategoriesService";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getPopularCategoriesDataView } from "@mcmc/App/Store/Selectors/PopularCategoriesSelector";
import { LazyImageProps } from "@mcmc/MobiusOverrides/LazyImage";
import Carousel from "@mcmc/Overrides/Components/Carousel";
import CloudinaryImage from "@mcmc/XDL/Components/Common/CloudinaryImage";
import { ellipsis, extendDataLayerWithEnvent, Roboto } from "@mcmc/XDL/Utilities";
import { transformationsMap } from "@mcmc/XDL/Utilities/cloudinary";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    numberOfCategoriesToDisplay = "numberOfCategoriesToDisplay",
    selectedCategoryIds = "selectedCategoryIds",
}

interface OwnProps {
    fields: {
        [fields.title]: string;
        [fields.numberOfCategoriesToDisplay]: string;
        [fields.selectedCategoryIds]: string[];
    };
}

const mapStateToProps = (state: McMcApplicationState, ownProps: WidgetProps) => {
    const popularCategoriesParameter = {
        count: ownProps.fields.numberOfCategoriesToDisplay,
        categories: ownProps.fields.selectedCategoryIds,
    };
    const popularCategoriesDataView = getPopularCategoriesDataView(state, popularCategoriesParameter);
    const shouldLoadPopularCategories = !popularCategoriesDataView.isLoading && !popularCategoriesDataView.value;

    return {
        allCategoriesPageUrl: getPageLinkByPageType(state, "CategoryListPage")?.url,
        popularCategoriesDataView,
        popularCategoriesParameter,
        shouldLoadPopularCategories,
        isAuthenticated: getSession(state).isAuthenticated,
    };
};

const mapDispatchToProps = {
    loadPopularCategories,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps & WidgetProps;

export interface CategoryListStyles {
    carouselCard?: LinkPresentationProps;
    carouselImage?: LazyImageProps;
    categoryName?: TypographyProps;
}

export const categoryListStyles: CategoryListStyles = {
    carouselCard: {
        color: "text",
        hoverStyle: { color: "text" },
        typographyProps: { ...Roboto, size: 14, lineHeight: "16px" },
        css: css`
            border: 1px solid ${getColor("common.ui3")};
            background: transparent;
            width: 160px;
            min-height: 136px;
            height: 100%;
            display: block;
            transition: border-color 0.2s;
            &:focus,
            &:hover {
                outline: none;
                border-color: ${getColor("text")};
            }
            > span {
                display: flex;
                flex-direction: column;
                height: 100%;
                width: 100%;
            }
        `,
    },
    carouselImage: {
        width: "100%",
        height: "90px",
    },

    categoryName: {
        ...Roboto,
        color: "text",
        css: css`
            ${ellipsis(2, 16)}
            margin: 5px 5px 10px;
            flex: 1;
            text-align: center;
        `,
    },
};

const styles = categoryListStyles;

class PopularCategories extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadPopularCategories, popularCategoriesParameter, shouldLoadPopularCategories } = this.props;

        if (shouldLoadPopularCategories) {
            loadPopularCategories(popularCategoriesParameter);
        }
    }

    render() {
        const { allCategoriesPageUrl, popularCategoriesDataView, id, isAuthenticated } = this.props;

        if (popularCategoriesDataView.isLoading || !popularCategoriesDataView.value) {
            return null;
        }

        const slides = mapCategoriesToSlides(popularCategoriesDataView.value, isAuthenticated);

        return (
            <StyledWrapper data-test-selector="brandGallery">
                {this.props.fields.title &&
                    createWidgetElement("SectionTitleWidget/SectionTitleWidget", {
                        fields: {
                            title: this.props.fields.title,
                            links: [
                                {
                                    fields: {
                                        destination: { type: "Url", value: allCategoriesPageUrl },
                                        overriddenTitle: translate("View All Categories"),
                                    },
                                },
                            ],
                        },
                    } as any)}
                <Carousel id={id} slideGap={30} slides={slides} />
            </StyledWrapper>
        );
    }
}

const mapCategoriesToSlides = (popularCategoriesData: PopularCategoriesCollectionModel, isAuthenticated: boolean) => {
    const handleCategoryClick = () => {
        extendDataLayerWithEnvent("popular-categories");
    };

    return popularCategoriesData.categories?.map(category => ({
        id: category.id,
        renderComponent: function CategoryCarouselRenderer() {
            return (
                <Link
                    href={category.categoryPath}
                    {...styles.carouselCard}
                    aria-label={category.imageAltText}
                    onClick={handleCategoryClick}
                    {...(!isAuthenticated ? { className: "popular-categories" } : {})}
                >
                    <CloudinaryImage
                        {...styles.carouselImage}
                        imageTransformations={transformationsMap.product_sm}
                        src={category.imagePath}
                        altText={category.imageAltText}
                    />
                    <Typography {...styles.categoryName}>{category.name || category.shortDescription}</Typography>
                </Link>
            );
        },
    }));
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PopularCategories),
    definition: {
        group: "Categories",
        icon: "Image",
        displayName: "Popular Categories",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Popular Categories",
                fieldType: "Contextual",
            },

            {
                name: fields.numberOfCategoriesToDisplay,
                displayName: "Number of Categories to display",
                editorTemplate: "DropDownField",
                defaultValue: "6",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                ],
                fieldType: "Contextual",
                dependentFields: [fields.selectedCategoryIds],
                isRequired: true,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "Contextual",
                validate: (value, item) => {
                    if (
                        item.fields.numberOfCategoriesToDisplay &&
                        item.fields.numberOfCategoriesToDisplay < value.length
                    ) {
                        return `The selected Categories should be less than ${item.fields.numberOfCategoriesToDisplay}`;
                    }
                    return null;
                },
            },
        ],
    },
};

export default widgetModule;
